import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import { useToast } from '../context/ToastContext';
import { registerStudent } from '../api';

const step1Schema = z.object({
  full_name: z.string().min(2, 'Name must be at least 2 characters'),
  college_name: z.string().min(2, 'College name is required'),
  college_email: z.string().email('Invalid college email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string()
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

const step2Schema = z.object({
  tnp_head_name: z.string().min(2, 'TnP Head Name is required'),
  tnp_head_phone: z.string().min(10, 'Valid phone number required'),
  branch: z.string().optional(),
  year: z.string().optional(),
});

const Register = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({});
  const navigate = useNavigate();
  const { addToast } = useToast();
  
  const { register: registerStep1, handleSubmit: handleStep1, formState: { errors: errors1 }, watch } = useForm({
    resolver: zodResolver(step1Schema)
  });
  
  const { register: registerStep2, handleSubmit: handleStep2, formState: { errors: errors2, isSubmitting } } = useForm({
    resolver: zodResolver(step2Schema)
  });

  const password = watch('password', '');
  
  const getPasswordStrength = (pass) => {
    let strength = 0;
    if (pass.length > 7) strength += 1;
    if (pass.match(/[a-z]+/)) strength += 1;
    if (pass.match(/[A-Z]+/)) strength += 1;
    if (pass.match(/[0-9]+/)) strength += 1;
    if (pass.match(/[$@#&!]+/)) strength += 1;
    
    if (pass.length === 0) return { label: '', class: '' };
    if (strength <= 2) return { label: 'Weak', class: 'strength-weak' };
    if (strength === 3) return { label: 'Fair', class: 'strength-fair' };
    if (strength === 4) return { label: 'Good', class: 'strength-good' };
    return { label: 'Strong', class: 'strength-strong' };
  };

  const strength = getPasswordStrength(password);

  const onStep1Submit = (data) => {
    setFormData({ ...data });
    setStep(2);
  };

  const onStep2Submit = async (data) => {
    const finalData = { ...formData, ...data };
    try {
      const res = await registerStudent(finalData);
      addToast('Registration successful! Please verify your email.', 'success');
      navigate(`/verify-email/${res.id || 'new'}`, { state: { email: finalData.college_email } });
    } catch (err) {
      addToast(err.response?.data?.detail || 'Registration failed. Email might already exist.', 'error');
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card auth-card-wide">
        <Link to="/" className="auth-logo justify-center">
          <div className="auth-logo-icon">M</div>
          <div>Million<span>Minds</span></div>
        </Link>
        
        <div className="text-center mb-6">
          <h1 className="auth-title">Create an Account</h1>
          <p className="auth-subtitle">Join Million Minds to land your dream job</p>
        </div>
        
        {/* Progress Bar */}
        <div className="flex items-center justify-between mb-8 relative">
          <div className="absolute left-0 top-1/2 w-full h-1 bg-gray-200 -z-10 transform -translate-y-1/2"></div>
          <div className="absolute left-0 top-1/2 h-1 bg-primary -z-10 transform -translate-y-1/2 transition-all duration-300" style={{ width: step === 1 ? '50%' : '100%' }}></div>
          
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 1 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500'}`}>1</div>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 2 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500'}`}>2</div>
        </div>

        {step === 1 ? (
          <form onSubmit={handleStep1(onStep1Submit)}>
            <div className="form-row">
              <Input
                label="Full Name"
                placeholder="John Doe"
                {...registerStep1('full_name')}
                error={errors1.full_name?.message}
              />
              <Input
                label="College Name"
                placeholder="University of Technology"
                {...registerStep1('college_name')}
                error={errors1.college_name?.message}
              />
            </div>
            
            <Input
              label="College Email"
              type="email"
              placeholder="john@college.edu"
              {...registerStep1('college_email')}
              error={errors1.college_email?.message}
            />
            
            <div className="form-row">
              <div className="w-full">
                <Input
                  label="Password"
                  type="password"
                  placeholder="••••••••"
                  {...registerStep1('password')}
                  error={errors1.password?.message}
                />
                {password && (
                  <div className={`password-strength ${strength.class}`}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="strength-label">Password strength</span>
                      <span className="strength-label font-semi">{strength.label}</span>
                    </div>
                    <div className="strength-bar"><div className="strength-fill"></div></div>
                  </div>
                )}
              </div>
              <Input
                label="Confirm Password"
                type="password"
                placeholder="••••••••"
                {...registerStep1('confirmPassword')}
                error={errors1.confirmPassword?.message}
              />
            </div>
            
            <div className="form-actions mt-8">
              <Button type="submit" variant="primary" className="w-full" size="lg">
                Continue
              </Button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleStep2(onStep2Submit)}>
            <div className="bg-primary-xlight border border-primary-light p-4 rounded-lg mb-6 text-sm text-primary-dark">
              <strong>College Verification:</strong> We need your TnP cell details to verify your college enrollment and approve your profile for placements.
            </div>
            
            <div className="form-row">
              <Input
                label="TnP Head Name"
                placeholder="Prof. Smith"
                {...registerStep2('tnp_head_name')}
                error={errors2.tnp_head_name?.message}
              />
              <Input
                label="TnP Head Phone"
                placeholder="+1 234 567 8900"
                {...registerStep2('tnp_head_phone')}
                error={errors2.tnp_head_phone?.message}
              />
            </div>
            
            <div className="form-row">
              <Input
                label="Branch/Specialization"
                placeholder="Computer Science"
                {...registerStep2('branch')}
                error={errors2.branch?.message}
                required={false}
              />
              <div className="form-group">
                <label className="form-label">Graduation Year</label>
                <select className="form-input select" {...registerStep2('year')}>
                  <option value="">Select Year</option>
                  <option value="1">1st Year</option>
                  <option value="2">2nd Year</option>
                  <option value="3">3rd Year</option>
                  <option value="4">4th Year</option>
                  <option value="graduated">Graduated</option>
                </select>
                {errors2.year && <span className="form-error">{errors2.year.message}</span>}
              </div>
            </div>
            
            <div className="form-actions mt-8">
              <Button type="button" variant="ghost" onClick={() => setStep(1)}>
                Back
              </Button>
              <Button type="submit" variant="primary" className="flex-1" size="lg" isLoading={isSubmitting}>
                Create Account
              </Button>
            </div>
          </form>
        )}
        
        <div className="auth-footer mt-6">
          Already have an account? <Link to="/login">Sign in</Link>
        </div>
      </div>
    </div>
  );
};

export default Register;
